/*
 * Resource Policy area (audit Phase 3.3). Replaces the three legacy
 * wwwroot/js/ResourcePolicy/*.js files with one module on the Phase 3.1 core.
 * No jQuery.
 *
 * Registered under Resource.Policy, the path the views already name. That
 * nesting is why this had to move in the same session as areas/resource.ts:
 * the legacy files hung off the same `Resource` global, and registering
 * `Resource` as an area turns off the registry's window fallback for
 * everything under it -- Resource.Policy.* included. ClientApp/test's wiring
 * guard caught exactly that.
 *
 * A Resource Policy is the access window (days + time-of-day) and the
 * client-certificate requirement a Resource is evaluated against, so the time
 * inputs are UTC on the wire and local in the field; serializeUtc and
 * applyUtcToInputs own that conversion.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, one } from '../core/dom';
import { initTimeWindow, serializeUtc } from '../core/forms';
import { modelStateSummary } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

function value(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
}

function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

/** The domain the list was rendered for; Policy/Resource/{domainId} re-renders it. */
function domainId(): string {
    return value('ResourcePolicyDomainId') || '0';
}

function reloadList(): Promise<void> {
    // The list is a PartialView, so a window.location.reload() would land on a
    // layout-less fragment. Re-fetch the domain route into the shell instead.
    return ajaxSwap({
        container: 'ViewContainer',
        url: `Policy/Resource/${domainId()}`,
        alertTitle: 'Failed to reload policies',
    });
}

/** Save button swaps its icon for a spinner while the request is in flight. */
function busy(formId: string, working: boolean): void {
    setHidden(`#${formId} .fa-save`, working);
    setHidden(`#${formId} .fa-spinner`, !working);
}

interface SaveOptions {
    formId: string;
    url: string;
    alertInto: string;
    errorTitle: string;
    okMessage: string;
    /** Edit scopes its validation messages to the modal; Create does not. */
    validationContainer?: string;
}

function savePolicy(options: SaveOptions): void {
    const form = one(options.formId) as HTMLFormElement | null;
    if (!form || !isValid(form)) return;
    busy(options.formId, true);
    void ajaxJson({
        url: options.url,
        method: 'POST',
        data: serializeUtc(form),
        alertInto: options.alertInto,
        errorTitle: options.errorTitle,
        onSuccess: () => {
            hideModal('ResourcePolicyModal');
            toast('success', options.okMessage);
            void reloadList();
        },
        onFailure: data => {
            const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
            if (modelState) setValidationModelState(modelState, options.validationContainer);
            toast('danger', options.errorTitle, summaryOf(data));
            return true;
        },
        onComplete: () => busy(options.formId, false),
    });
}

export const List = {
    /**
     * Row click -> the Edit modal. No title is passed: Edit.cshtml owns the
     * whole modal-dialog, and a title makes showModal wrap a second one round it.
     */
    RowClick(id: number | string): false {
        if (!(Number(id) > 0)) return false;
        void ajaxModal({
            modal: 'ResourcePolicyModal',
            url: `Policy/Resource/Edit/${id}`,
            formId: 'EditResourcePolicyForm',
            focusId: 'Name',
            errorTitle: `Resource Policy ${id}`,
            errorMessage: 'Resource Policy Edit failed to execute:',
        });
        return false;
    },

    DisplayCreateModal(): void {
        void ajaxModal({
            modal: 'ResourcePolicyModal',
            url: `Policy/Resource/Create?domainId=${encodeURIComponent(value('ResourcePolicyDomainId'))}`,
            formId: 'CreateResourcePolicyForm',
            focusId: 'Name',
            errorTitle: 'New Resource Policy',
            errorMessage: 'New Resource Policy View failed to execute:',
        });
    },

    DeleteRow(id: number | string): false {
        if (!(Number(id) > 0)) return false;
        void confirm({
            title: 'Delete Resource Policy',
            html: 'Delete this Policy?<br/><br/>'
                + '<span class="text-danger"><strong>WARNING:</strong> '
                + 'any users associated with this policy will receive default policy handling until reassigned.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Policy/Resource/${id}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to delete policy',
                    onSuccess: () => {
                        toast('success', 'Policy deleted');
                        for (const row of all(`tr[data-id='${id}']`)) row.remove();
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to delete policy', summaryOf(data));
                        return true;
                    },
                    onError: (xhr, _status, error) => {
                        toast('danger', 'Failed to delete policy', xhr?.statusText || error);
                        return true;
                    },
                });
            },
        });
        return false;
    },
};

export const Create = {
    SaveClick(): void {
        savePolicy({
            formId: 'CreateResourcePolicyForm',
            url: 'Policy/Resource/Create',
            alertInto: 'ResourcePolicyCreateMessageContainer',
            errorTitle: 'Failed to create policy',
            okMessage: 'Policy created',
        });
    },
};

export const Edit = {
    SaveClick(): void {
        savePolicy({
            // The PolicyId travels in the form body, so the route needs no id segment.
            formId: 'EditResourcePolicyForm',
            url: 'Policy/Resource/Edit',
            alertInto: 'ResourcePolicyEditMessageContainer',
            errorTitle: 'Failed to save policy',
            okMessage: 'Policy saved',
            validationContainer: 'ResourcePolicyEditMessageContainer',
        });
    },

    DeletePolicy(): void {
        const policyId = value('PolicyId');
        if (!policyId) return;
        void confirm({
            title: 'Delete Resource Policy',
            html: 'Delete this Resource Policy?<br/><br/>'
                + '<span class="text-danger"><strong>WARNING:</strong> '
                + 'this may affect access permissions for any associated users, applications, or resources.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Policy/Resource/${policyId}`,
                    method: 'DELETE',
                    alertInto: 'ResourcePolicyEditMessageContainer',
                    errorTitle: 'An error occurred attempting to delete the policy',
                    onSuccess: () => {
                        hideModal('ResourcePolicyModal');
                        toast('success', 'Policy deleted');
                        for (const row of all(`tr[data-id='${policyId}']`)) row.remove();
                    },
                });
            },
        });
    },
};

/**
 * Prepare the access-window controls whenever a policy form lands.
 *
 * An enhancer, not an onSuccess callback: the legacy SetTimePickers pair was
 * invoked by whichever handler happened to open the modal, so a form reached
 * any other way came up with both time inputs enabled and the "no time
 * restriction" checkbox out of step with them.
 */
export function enhancePolicyForms(root: ParentNode): void {
    const match = root instanceof Element && root.matches('#AccessWindowTimes')
        ? root
        : root.querySelector('#AccessWindowTimes');
    if (!match) return;
    const start = document.getElementById('AccessTimeStart');
    const end = document.getElementById('AccessTimeEnd');
    start?.setAttribute('placeholder', 'Start');
    end?.setAttribute('placeholder', 'End');
    initTimeWindow('AccessNoTimeWindow', '#AccessWindowTimes');
}
